const CONSENT_STORAGE_KEY = "store_analytics_consent_v1";
const ACCEPTED = "accepted";
const REJECTED = "rejected";
// Upper bound for events held while PostHog initializes. Beyond it, new events are dropped.
export const MAX_PENDING_ANALYTICS_EVENTS = 50;

const EVENT_SCHEMAS = {
  product_viewed: {
    required: ["product_id"],
    properties: {
      product_id: safeString,
      product_category: safeString,
      currency: safeCurrency,
    },
  },
  product_added_to_cart: {
    required: ["product_id", "item_count"],
    properties: {
      product_id: safeString,
      product_category: safeString,
      item_count: safeItemCount,
      currency: safeCurrency,
    },
  },
  cart_viewed: {
    required: ["item_count"],
    properties: { item_count: safeItemCount, currency: safeCurrency },
  },
  checkout_started: {
    required: ["item_count"],
    properties: { item_count: safeItemCount, currency: safeCurrency },
  },
  delivery_check_completed: {
    required: ["availability"],
    properties: { availability: safeAvailability },
  },
  payment_attempted: {
    required: ["payment_method"],
    properties: {
      payment_method: safePaymentMethod,
      item_count: safeItemCount,
      currency: safeCurrency,
    },
  },
  checkout_error: {
    required: ["error_category"],
    properties: { error_category: safeErrorCategory },
  },
};

let consentChoice;
let posthogInstance = null;
let posthogOptedIn = false;
let analyticsRequestController = null;
let initializationPromise = null;
let initializationRevision = null;
let consentRevision = 0;
let pendingEventQueue = [];

function safeString(value) {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim().slice(0, 100)
    : undefined;
}

function safeItemCount(value) {
  return Number.isInteger(value) && value >= 0 && value <= 10000
    ? value
    : undefined;
}

function safeCurrency(value) {
  return value === "INR" ? value : undefined;
}

function safeAvailability(value) {
  return ["available", "unavailable", "unknown"].includes(value) ? value : undefined;
}

function safePaymentMethod(value) {
  return ["RAZORPAY", "COD"].includes(value) ? value : undefined;
}

function safeErrorCategory(value) {
  return [
    "order_submission",
    "payment_setup",
    "payment_verification",
    "payment_failed",
  ].includes(value)
    ? value
    : undefined;
}

function sanitizeProperties(schema, properties) {
  const safeProperties = {};
  for (const [key, validate] of Object.entries(schema.properties)) {
    const value = validate(properties?.[key]);
    if (value !== undefined) safeProperties[key] = value;
  }
  return safeProperties;
}

// Anonymous transport fields PostHog needs to accept an event. Without `token`
// and `distinct_id` the event cannot be ingested. Page URL, referrer, browser and
// device properties are deliberately NOT included.
const SDK_TRANSPORT_PROPERTIES = ["token", "distinct_id"];

function beforeSend(event) {
  const schema = EVENT_SCHEMAS[event?.event];
  if (!schema || consentChoice !== ACCEPTED) return null;
  try {
    const properties = sanitizeProperties(schema, event.properties);
    if (schema.required.some((key) => properties[key] === undefined)) return null;
    for (const key of SDK_TRANSPORT_PROPERTIES) {
      const value = event.properties?.[key];
      if (typeof value === "string" && value.length > 0) properties[key] = value;
    }
    return { ...event, properties };
  } catch (error) {
    console.warn("PostHog event properties could not be sanitized:", error);
    return null;
  }
}

export function isAnalyticsExcludedRoute(path = window.location.pathname) {
  return /^\/admin(?:\/|$)/.test(path)
    || /^\/(?:login|register|forgot-password|reset-password|account(?:\/|$)|track-order(?:\/|$)|order-success(?:\/|$))/.test(path);
}

export function getAnalyticsConsent() {
  if (consentChoice !== undefined) return consentChoice;
  try {
    const value = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    consentChoice = value === ACCEPTED || value === REJECTED ? value : null;
    return consentChoice;
  } catch {
    return null;
  }
}

function readPostHogConfig() {
  const key = process.env.REACT_APP_POSTHOG_KEY?.trim();
  const host = process.env.REACT_APP_POSTHOG_HOST?.trim();
  if (!key || !host) return null;

  try {
    const parsedHost = new URL(host);
    if (parsedHost.protocol !== "https:" && parsedHost.protocol !== "http:") {
      console.warn("PostHog analytics is disabled because its host is invalid.");
      return null;
    }
    return { key, host: parsedHost.origin };
  } catch (error) {
    console.warn("PostHog analytics is disabled because its host is invalid:", error);
    return null;
  }
}

function createAnalyticsRequestController() {
  if (typeof window.AbortController !== "function") {
    console.warn("PostHog analytics is disabled because request cancellation is unavailable.");
    return null;
  }
  return new window.AbortController();
}

function buildSafeProperties(eventName, properties) {
  const schema = EVENT_SCHEMAS[eventName];
  if (!schema) return null;
  const safeProperties = sanitizeProperties(schema, properties);
  if (schema.required.some((key) => safeProperties[key] === undefined)) return null;
  return safeProperties;
}

function enqueuePendingEvent(eventName, safeProperties) {
  // When the queue is full the newest event is dropped, so the events that are
  // kept stay in their original chronological order.
  if (pendingEventQueue.length >= MAX_PENDING_ANALYTICS_EVENTS) return;
  pendingEventQueue.push({ eventName, safeProperties });
}

function flushPendingEvents() {
  // Take ownership of the queue first so every event is sent at most once.
  const events = pendingEventQueue;
  pendingEventQueue = [];
  if (!posthogInstance || !posthogOptedIn || consentChoice !== ACCEPTED) return;
  // Privacy policy: events are never sent while on an excluded route. Queued
  // events are discarded (not deferred) so they cannot be sent from a later page.
  if (isAnalyticsExcludedRoute()) return;
  for (const { eventName, safeProperties } of events) {
    try {
      const flushProperties = buildSafeProperties(eventName, safeProperties);
      if (!flushProperties) continue;
      posthogInstance.capture(eventName, flushProperties);
    } catch (error) {
      console.warn(`PostHog queued event "${eventName}" could not be captured:`, error);
    }
  }
}

function clearPendingEvents() {
  pendingEventQueue = [];
}

function abortAnalyticsRequests() {
  try {
    analyticsRequestController?.abort();
  } catch (error) {
    console.warn("Pending PostHog requests could not be cancelled:", error);
  } finally {
    analyticsRequestController = null;
  }
}

function initializePostHog() {
  if (consentChoice !== ACCEPTED || isAnalyticsExcludedRoute()) return Promise.resolve(false);
  if (posthogInstance) {
    if (!posthogOptedIn) {
      analyticsRequestController = createAnalyticsRequestController();
      if (!analyticsRequestController) return Promise.resolve(false);
      try {
        posthogInstance.set_config({
          advanced_disable_flags: false,
          fetch_options: { signal: analyticsRequestController.signal },
        });
        posthogInstance.opt_in_capturing({ captureEventName: false });
        posthogOptedIn = true;
      } catch (error) {
        abortAnalyticsRequests();
        console.warn("PostHog analytics consent could not be applied:", error);
        return Promise.resolve(false);
      }
    }
    return Promise.resolve(true);
  }
  if (initializationPromise) {
    if (initializationRevision !== consentRevision) {
      return initializationPromise.then(() => initializePostHog());
    }
    return initializationPromise;
  }

  const config = readPostHogConfig();
  if (!config) return Promise.resolve(false);

  const revision = consentRevision;
  initializationRevision = revision;
  const pendingInitialization = import("posthog-js")
    .then(({ default: posthog }) => {
      if (consentChoice !== ACCEPTED || revision !== consentRevision) return false;
      analyticsRequestController = createAnalyticsRequestController();
      if (!analyticsRequestController) {
        clearPendingEvents();
        return false;
      }
      posthog.init(config.key, {
        api_host: config.host,
        api_transport: "fetch",
        disable_beacon: true,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        opt_out_capturing_by_default: true,
        persistence: "memory",
        request_batching: false,
        fetch_options: { signal: analyticsRequestController.signal },
        before_send: beforeSend,
      });
      posthogInstance = posthog;
      posthog.opt_in_capturing({ captureEventName: false });
      posthogOptedIn = true;
      flushPendingEvents();
      return true;
    })
    .catch((error) => {
      abortAnalyticsRequests();
      clearPendingEvents();
      console.warn("PostHog analytics could not be initialized:", error);
      return false;
    })
    .finally(() => {
      if (initializationPromise === pendingInitialization) {
        initializationPromise = null;
        initializationRevision = null;
      }
    });
  initializationPromise = pendingInitialization;
  return initializationPromise;
}

export async function restoreAnalyticsConsent() {
  if (getAnalyticsConsent() !== ACCEPTED) return false;
  return initializePostHog();
}

export async function setAnalyticsConsent(choice) {
  if (choice !== ACCEPTED && choice !== REJECTED) return false;

  if (choice === REJECTED) {
    consentChoice = REJECTED;
    consentRevision += 1;
    clearPendingEvents();
    abortAnalyticsRequests();
    if (posthogInstance) {
      try {
        posthogInstance.opt_out_capturing();
      } catch (error) {
        console.warn("PostHog analytics opt-out could not be applied:", error);
      }
      try {
        posthogInstance.set_config({ advanced_disable_flags: true });
      } catch (error) {
        console.warn("PostHog feature flags could not be disabled:", error);
      }
      posthogOptedIn = false;
    }
  }

  try {
    if (choice === REJECTED) {
      try {
        window.localStorage.removeItem(CONSENT_STORAGE_KEY);
      } catch (error) {
        console.warn("Previous analytics consent could not be cleared:", error);
      }
    }
    window.localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch (error) {
    console.warn("Analytics consent could not be saved:", error);
    return false;
  }

  consentChoice = choice;
  if (choice === ACCEPTED) {
    await initializePostHog();
  }
  return true;
}

/**
 * @param {keyof typeof EVENT_SCHEMAS} eventName
 * @param {Record<string, unknown>} [properties]
 */
export function captureAnalyticsEvent(eventName, properties = {}) {
  if (
    consentChoice !== ACCEPTED
    || !EVENT_SCHEMAS[eventName]
    || isAnalyticsExcludedRoute()
  ) return;

  try {
    const safeProperties = buildSafeProperties(eventName, properties);
    if (!safeProperties) return;

    if (!posthogInstance) {
      if (initializationPromise) enqueuePendingEvent(eventName, safeProperties);
      return;
    }

    posthogInstance.capture(eventName, safeProperties);
  } catch (error) {
    console.warn(`PostHog event "${eventName}" could not be captured:`, error);
  }
}
